<template>
  <BaseSheet :isOpen="isOpen" size="lg" :title="componentTitle" :subtitle="t('editcomponent.sheet.subtitle')" @close="handleClose">
    <template #header-extra>
      <!-- Period navigation -->
      <div v-if="componentType !== 'custom'" class="px-5 xl:px-6 pb-4 xl:pt-5">
        <div class="ec-period-card rounded-2xl relative overflow-hidden">
          <div class="px-4 pt-3.5 pb-3">
            <!-- Nav row -->
            <div class="flex items-center justify-between">
              <button @click="shiftPeriod(-1)"
                class="ec-arrow-btn w-9 h-9 rounded-xl flex items-center justify-center active:scale-90 transition-all duration-150">
                <ChevronLeftIcon class="h-4 w-4 stroke-[2.5]" />
              </button>

              <div class="flex-1 mx-3 text-center relative overflow-hidden h-6">
                <Transition :name="periodTransition" mode="out-in">
                  <span :key="periodLabel"
                    class="text-[15px] font-bold tracking-tight text-foreground absolute inset-0 flex items-center justify-center">
                    {{ periodLabel }}
                  </span>
                </Transition>
              </div>

              <button @click="shiftPeriod(1)" :disabled="periodOffset >= 0"
                class="ec-arrow-btn w-9 h-9 rounded-xl flex items-center justify-center active:scale-90 transition-all duration-150 disabled:opacity-25">
                <ChevronRightIcon class="h-4 w-4 stroke-[2.5]" />
              </button>
            </div>

            <!-- Reset to current period -->
            <Transition name="ec-offset-reveal">
              <div v-if="periodOffset !== 0" class="flex items-center justify-center mt-2.5">
                <button @click="resetPeriod"
                  class="ec-reset-pill text-[11px] font-semibold px-2.5 py-0.5 rounded-full active:scale-95 transition-all">
                  {{ t('editcomponent.sheet.reset_period') }}
                </button>
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </template>

    <!-- Scrollable content -->
    <div class="flex-1 overflow-y-auto px-5 xl:px-6 min-h-0">

      <!-- Settings sections -->
      <div class="flex flex-col gap-1">
        <!-- Type -->
        <button @click="toggleSection('type')"
          class="flex items-center justify-between py-3 w-full ec-section-btn">
          <div class="flex items-center gap-2.5">
            <div class="w-8 h-8 rounded-lg flex items-center justify-center ec-section-icon">
              <CalendarDaysIcon class="h-4 w-4" style="color: #06C8FF;" />
            </div>
            <div class="text-left">
              <p class="text-[13px] font-semibold text-foreground">{{ t('editcomponent.type.label') }}</p>
              <p class="text-[11px] text-muted-foreground">{{ t(`newcomponent.type.${newComponentStore.newComponent.type}`) }}</p>
            </div>
          </div>
          <ChevronDownIcon class="h-4 w-4 text-muted-foreground transition-transform duration-200"
            :class="{ 'rotate-180': activeSection === 'type' }" />
        </button>
        <Transition name="slide-down">
          <div v-if="activeSection === 'type'" class="pb-3 pl-[42px]">
            <ComponentTypeForm />
          </div>
        </Transition>

        <div class="ec-divider"></div>

        <!-- Date -->
        <button @click="toggleSection('date')"
          class="flex items-center justify-between py-3 w-full ec-section-btn">
          <div class="flex items-center gap-2.5">
            <div class="w-8 h-8 rounded-lg flex items-center justify-center ec-section-icon">
              <ClockIcon class="h-4 w-4" style="color: #06C8FF;" />
            </div>
            <div class="text-left">
              <p class="text-[13px] font-semibold text-foreground">{{ t('editcomponent.date.label') }}</p>
              <p class="text-[11px] text-muted-foreground">{{ dateSummary }}</p>
            </div>
          </div>
          <ChevronDownIcon class="h-4 w-4 text-muted-foreground transition-transform duration-200"
            :class="{ 'rotate-180': activeSection === 'date' }" />
        </button>
        <Transition name="slide-down">
          <div v-if="activeSection === 'date'" class="pb-3 pl-[42px]">
            <ComponentDateForm />
          </div>
        </Transition>

        <div class="ec-divider"></div>

        <!-- Dataset -->
        <button @click="toggleSection('dataset')"
          class="flex items-center justify-between py-3 w-full ec-section-btn">
          <div class="flex items-center gap-2.5">
            <div class="w-8 h-8 rounded-lg flex items-center justify-center ec-section-icon">
              <CircleStackIcon class="h-4 w-4" style="color: #06C8FF;" />
            </div>
            <div class="text-left">
              <p class="text-[13px] font-semibold text-foreground">{{ t('editcomponent.dataset.label') }}</p>
              <p class="text-[11px] text-muted-foreground">
                {{ t(`newcomponent.datasets.origin.${newComponentStore.newComponent.dataOrigin}`) }}
                · {{ t(`newcomponent.datasets.type.${newComponentStore.newComponent.dataType === 'deposit' ? 'invoices' : 'receipts'}`) }}
              </p>
            </div>
          </div>
          <ChevronDownIcon class="h-4 w-4 text-muted-foreground transition-transform duration-200"
            :class="{ 'rotate-180': activeSection === 'dataset' }" />
        </button>
        <Transition name="slide-down">
          <div v-if="activeSection === 'dataset'" class="pb-3 pl-[42px]">
            <ComponentDatasetForm />
          </div>
        </Transition>

        <div class="ec-divider"></div>

        <!-- Chart / Title -->
        <button @click="toggleSection('chart')"
          class="flex items-center justify-between py-3 w-full ec-section-btn">
          <div class="flex items-center gap-2.5">
            <div class="w-8 h-8 rounded-lg flex items-center justify-center ec-section-icon">
              <ChartBarIcon class="h-4 w-4" style="color: #06C8FF;" />
            </div>
            <div class="text-left">
              <p class="text-[13px] font-semibold text-foreground">{{ t('editcomponent.chart.label') }}</p>
              <p class="text-[11px] text-muted-foreground">{{ newComponentStore.newComponent.title || '—' }}</p>
            </div>
          </div>
          <ChevronDownIcon class="h-4 w-4 text-muted-foreground transition-transform duration-200"
            :class="{ 'rotate-180': activeSection === 'chart' }" />
        </button>
        <Transition name="slide-down">
          <div v-if="activeSection === 'chart'" class="pb-3 pl-[42px]">
            <ComponentChartForm :edit="true" />
          </div>
        </Transition>
      </div>

      <div class="h-4"></div>
    </div>

    <!-- Footer -->
    <div class="flex-shrink-0 px-5 xl:px-6 py-4 cs-footer">
      <div class="flex items-center gap-3">
        <!-- Delete (left) -->
        <button @click="confirmDeleteIsOpen = true"
          class="h-[48px] xl:h-[40px] px-5 rounded-[14px] xl:rounded-[10px] text-[13px] font-semibold ec-btn-delete active:scale-[0.97] transition-all duration-150">
          <TrashIcon class="h-4 w-4" />
        </button>

        <!-- Spacer (desktop: push cancel+save to right) -->
        <div class="hidden xl:block flex-1"></div>

        <!-- Cancel (desktop only) -->
        <button @click="handleClose"
          class="hidden xl:block h-[40px] px-4 rounded-[10px] text-[13px] font-semibold text-muted-foreground hover:text-foreground hover:bg-[var(--surface-hover)] transition-all duration-150">
          {{ t('Cancel') }}
        </button>

        <!-- Save -->
        <button @click="save" :disabled="saving"
          class="flex-1 xl:flex-none h-[48px] xl:h-[40px] xl:px-8 rounded-[14px] xl:rounded-[10px] text-[14px] xl:text-[13px] font-bold text-white cs-btn-cta flex items-center justify-center gap-2 active:scale-[0.97] transition-all duration-150 disabled:opacity-70">
          <svg v-if="saving" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg"
            fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span v-if="!saving">{{ t('Save') }}</span>
        </button>
      </div>
    </div>

    <!-- Delete confirmation dialog -->
    <FormDialog :isOpen="confirmDeleteIsOpen" :title="t('editcomponent.delete')"
      :sending="deleting" :confirmText="t('Delete')" :closeText="t('Cancel')"
      color="danger" @close="confirmDeleteIsOpen = false" @submit-form="deleteComponent">
      {{ t('editcomponent.confirm_delete') }}
    </FormDialog>
  </BaseSheet>
</template>

<script setup lang="ts">
import { ref, computed, watch, type Ref } from 'vue'
import {
  ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon,
  CalendarDaysIcon, ClockIcon, CircleStackIcon, ChartBarIcon, TrashIcon
} from '@heroicons/vue/24/outline'
import { useI18n } from 'vue-i18n'
import BaseSheet from '../dialogs/BaseSheet.vue'
import { useNewComponentStore } from '../../stores/newComponentStore'
import { useSalesReportsStore } from '../../stores/salesReports'
import { useSnackbarStore } from '../../stores/snackbarStore'
import { usePeriodShift } from '../../composables/usePeriodShift'
import type { SalesReport } from '../../models/SalesReport'
import ComponentTypeForm from '../form/ComponentTypeForm.vue'
import ComponentDateForm from '../form/ComponentDateForm.vue'
import ComponentDatasetForm from '../form/ComponentDatasetForm.vue'
import ComponentChartForm from '../form/ComponentChartForm.vue'
import FormDialog from '../dialogs/FormDialog.vue'

const { t } = useI18n()
const newComponentStore = useNewComponentStore()
const salesReportsStore = useSalesReportsStore()
const snackbarStore = useSnackbarStore()

const props = defineProps<{
  isOpen: boolean
  componentId: string
}>()

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
}>()

const activeSection = ref<string | null>(null)
const saving = ref(false)
const deleting = ref(false)
const confirmDeleteIsOpen = ref(false)

const componentTitle = computed(() => newComponentStore.newComponent.title || '—')
const componentType = computed(() => newComponentStore.newComponent.type)

// Find the SalesReport instance for this component to share period state
const report = computed(() => {
  for (const center of salesReportsStore.salesReportsByCenter) {
    const sr = center.salesReports.find(sr => sr.id === props.componentId)
    if (sr) return sr
  }
  return undefined
})

const { periodOffset, periodTransition, periodLabel, shiftedDate, shiftPeriod, resetPeriod } = usePeriodShift(report as Ref<SalesReport | undefined>, 'ec')

const dateSummary = computed(() => {
  const comp = newComponentStore.newComponent
  if (comp.type === 'custom') {
    return comp.customStartDate && comp.customEndDate
      ? `${comp.customStartDate} — ${comp.customEndDate}`
      : '—'
  }
  const labels: Record<string, string> = {
    daily: t('newcomponent.dates.day', comp.periodBeforeDate),
    weekly: t('newcomponent.dates.week', comp.periodBeforeDate),
    monthly: t('newcomponent.dates.month', comp.periodBeforeDate),
    yearly: t('newcomponent.dates.year', comp.periodBeforeDate),
  }
  return `${comp.periodBeforeDate} ${labels[comp.type] || ''}`
})

const applyPeriodShift = async (savedOffset: number, savedDate: import('luxon').DateTime) => {
  const type = newComponentStore.newComponent.type
  if (type === 'custom' || savedOffset === 0) return

  // Apply shift to all reports with this componentId across all centers
  const fetchPromises: Promise<void>[] = []
  salesReportsStore.salesReportsByCenter.forEach((center) => {
    center.salesReports.forEach((sr) => {
      if (sr.id === props.componentId) {
        sr.periodOffset = savedOffset
        sr.setDate(savedDate)
        sr.resetPreviousDate()
        sr.data = null
        fetchPromises.push(sr.fetchAll(center.centerId, true))
      }
    })
  })

  await Promise.allSettled(fetchPromises)
}

const toggleSection = (section: string) => {
  activeSection.value = activeSection.value === section ? null : section
}

// Offset captured when the dialog opens — used to undo unsaved navigation on cancel
const openingOffset = ref(0)

const save = async () => {
  saving.value = true
  const success = await newComponentStore.send()
  saving.value = false

  if (success) {
    const savedOffset = periodOffset.value
    const savedDate = shiftedDate.value
    salesReportsStore.addToRefreshList(props.componentId)
    snackbarStore.addSnackbar(t('editcomponent.snackbar.updated'), 'default')
    // Skip auto-fetch when period is shifted to avoid race with applyPeriodShift
    await salesReportsStore.refreshCentersComponents(savedOffset !== 0)
    await applyPeriodShift(savedOffset, savedDate)
    // Update so handleClose doesn't undo the saved shift
    openingOffset.value = savedOffset
    handleClose()
  }
}

const deleteComponent = async () => {
  deleting.value = true
  const success = await salesReportsStore.deleteReport(props.componentId)
  deleting.value = false

  if (success) {
    confirmDeleteIsOpen.value = false
    snackbarStore.addSnackbar(t('editcomponent.snackbar.deleted'), 'danger')
    handleClose()
  }
}

const handleClose = () => {
  // Restore offset to what it was when the dialog opened (undoes unsaved navigation)
  periodOffset.value = openingOffset.value
  emit('update:isOpen', false)
  // activeSection is reset on reopen (watch isOpen), not here —
  // collapsing content during the leave transition causes visual jank.
}

// Load component data when sheet opens
watch(() => props.isOpen, (isOpen) => {
  if (isOpen && props.componentId) {
    newComponentStore.loadEditing(props.componentId)
    activeSection.value = null
    openingOffset.value = periodOffset.value
  }
})
</script>

<style scoped>
/* ── Light mode ── */
.cs-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.cs-btn-cta {
  background: linear-gradient(135deg, #06C8FF 0%, #009FE3 100%);
  box-shadow: 0 6px 24px rgba(6, 200, 255, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

/* Period card */
.ec-period-card {
  background: var(--card);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.04),
    0 4px 14px rgba(0, 0, 0, 0.03);
}
.ec-arrow-btn {
  color: var(--foreground);
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
}
.ec-arrow-btn:active:not(:disabled) {
  background: rgba(6, 200, 255, 0.10);
  border-color: rgba(6, 200, 255, 0.20);
}
.ec-reset-pill {
  color: #06C8FF;
  background: rgba(6, 200, 255, 0.08);
  border: 1px solid rgba(6, 200, 255, 0.15);
}
.ec-reset-pill:active {
  background: rgba(6, 200, 255, 0.15);
}

/* Shimmer */
.ec-shimmer {
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(6, 200, 255, 0.06) 30%,
    rgba(6, 200, 255, 0.12) 50%,
    rgba(6, 200, 255, 0.06) 70%,
    transparent 100%
  );
  background-size: 250% 100%;
  animation: ec-shimmer-sweep 1.4s ease-in-out infinite;
}
@keyframes ec-shimmer-sweep {
  0% { background-position: 150% 0; }
  100% { background-position: -50% 0; }
}

/* Section icons */
.ec-section-icon {
  background: rgba(6, 200, 255, 0.08);
}
.ec-section-btn {
  text-align: left;
}
.ec-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
}

/* Delete button */
.ec-btn-delete {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.06);
  border: 1px solid rgba(239, 68, 68, 0.12);
}
.ec-btn-delete:active {
  background: rgba(239, 68, 68, 0.12);
}

/* ── Dark mode ── */
:is(.dark *).cs-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
:is(.dark *).cs-btn-cta {
  box-shadow: 0 4px 20px rgba(6, 200, 255, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.10);
}
:is(.dark *).ec-period-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.15),
    0 4px 14px rgba(0, 0, 0, 0.10);
}
:is(.dark *).ec-arrow-btn {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.06);
}
:is(.dark *).ec-arrow-btn:active:not(:disabled) {
  background: rgba(6, 200, 255, 0.12);
  border-color: rgba(6, 200, 255, 0.22);
}
:is(.dark *).ec-reset-pill {
  background: rgba(6, 200, 255, 0.10);
  border: 1px solid rgba(6, 200, 255, 0.18);
}
:is(.dark *).ec-shimmer {
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(6, 200, 255, 0.05) 30%,
    rgba(6, 200, 255, 0.10) 50%,
    rgba(6, 200, 255, 0.05) 70%,
    transparent 100%
  );
  background-size: 250% 100%;
  animation: ec-shimmer-sweep 1.4s ease-in-out infinite;
}
:is(.dark *).ec-section-icon {
  background: rgba(6, 200, 255, 0.10);
}
:is(.dark *).ec-divider {
  background: rgba(255, 255, 255, 0.06);
}
:is(.dark *).ec-btn-delete {
  color: #f87171;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.15);
}
:is(.dark *).ec-btn-delete:active {
  background: rgba(239, 68, 68, 0.15);
}

/* ── Slide transitions ── */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}
.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  max-height: 0;
}
.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  max-height: 500px;
}

/* ── Period label slide ── */
.ec-label-slide-left-enter-active,
.ec-label-slide-left-leave-active,
.ec-label-slide-right-enter-active,
.ec-label-slide-right-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.ec-label-slide-left-enter-from {
  opacity: 0;
  transform: translateX(16px);
}
.ec-label-slide-left-leave-to {
  opacity: 0;
  transform: translateX(-16px);
}
.ec-label-slide-right-enter-from {
  opacity: 0;
  transform: translateX(-16px);
}
.ec-label-slide-right-leave-to {
  opacity: 0;
  transform: translateX(16px);
}

/* ── Offset indicator reveal ── */
.ec-offset-reveal-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.ec-offset-reveal-leave-active {
  transition: all 0.2s ease;
}
.ec-offset-reveal-enter-from {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
}
.ec-offset-reveal-enter-to {
  opacity: 1;
  max-height: 32px;
  margin-top: 10px;
}
.ec-offset-reveal-leave-from {
  opacity: 1;
  max-height: 32px;
  margin-top: 10px;
}
.ec-offset-reveal-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
}
</style>
